// Role permissions grouped by the sidebar page they control.
// Keep in sync with ROLE_PERMISSION_FIELDS in backend/Models/Role.js.
// `requires` mirrors backend/utils/permissionValidator.js: ticking a permission
// also ticks what it needs, and unticking one clears what depends on it.

export const ROLE_PERMISSION_GROUPS = [
  {
    title: "Dashboard",
    permissions: [{ key: "dashboard", label: "Main Dashboard" }],
  },
  {
    title: "Admins (Active Admins)",
    permissions: [
      { key: "usersView", label: "View Admins" },
      { key: "usersAdd", label: "Add Admins", requires: ["usersView"] },
      { key: "usersUpdate", label: "Update Admins / Force Logout", requires: ["usersView"] },
      { key: "usersDelete", label: "Delete Admins", requires: ["usersView"] },
    ],
  },
  {
    title: "Players (All Players)",
    permissions: [
      { key: "playersView", label: "View Players" },
      { key: "playersFreeze", label: "Freeze / Ban Players", requires: ["playersView"] },
      { key: "playersViewSensitive", label: "View Full Phone Number", requires: ["playersView"] },
      { key: "playersExport", label: "Export Player List", requires: ["playersView"] },
      { key: "playersDelete", label: "Delete Players", requires: ["playersView"] },
    ],
  },
  {
    title: "Venues",
    permissions: [
      { key: "locationsView", label: "View Locations" },
      { key: "locationsAdd", label: "Add Locations", requires: ["locationsView"] },
      { key: "locationsUpdate", label: "Update / Deactivate Locations", requires: ["locationsView"] },
      { key: "clubsView", label: "View Clubs" },
      { key: "clubsAdd", label: "Add Clubs", requires: ["clubsView"] },
      { key: "clubsUpdate", label: "Update Clubs", requires: ["clubsView"] },
      { key: "clubsDelete", label: "Delete Clubs", requires: ["clubsView", "clubsUpdate"] },
      {
        key: "adminClubAssignment",
        label: "Assign Admins to Clubs",
        requires: ["clubsView", "usersView"],
      },
    ],
  },
  {
    title: "Administration",
    permissions: [
      { key: "rolesView", label: "View Roles" },
      { key: "rolesAdd", label: "Add Roles", requires: ["rolesView"] },
      { key: "rolesUpdate", label: "Update Roles", requires: ["rolesView"] },
      { key: "rolesDelete", label: "Delete Roles", requires: ["rolesView"] },
      { key: "auditTrailView", label: "Audit Trail" },
      { key: "invoiceTemplateManage", label: "Invoice Template" },
    ],
  },
  {
    title: "Check-in & Security",
    permissions: [
      { key: "qrScanner", label: "QR Scanner" },
      { key: "checkinListView", label: "Check-In List / Manual Checkout" },
      { key: "manualOTP", label: "Player OTP" },
      { key: "manualKYC", label: "Manual KYC" },
    ],
  },
  {
    title: "Money (Admin Transactions)",
    permissions: [
      { key: "transactionsView", label: "View Transactions" },
      { key: "transactionsAdd", label: "Add Transactions / Settle Bills", requires: ["transactionsView"] },
      { key: "transactionsUpdate", label: "Approve / Mark Paid", requires: ["transactionsView"] },
      {
        key: "transactionsDelete",
        label: "Reject / Cancel Transactions",
        requires: ["transactionsView", "transactionsUpdate"],
      },
    ],
  },
  {
    title: "Reports",
    permissions: [
      { key: "reports", label: "Financial, Affiliated & Club Ledger", requires: ["dashboard"] },
      { key: "tablePlayerReportView", label: "Player & Table Report" },
    ],
  },
  {
    title: "Messaging",
    permissions: [
      { key: "messagingView", label: "View Messages & Templates" },
      { key: "messagingCreate", label: "Create Templates & Campaigns", requires: ["messagingView"] },
      { key: "messagingSend", label: "Send Messages", requires: ["messagingView"] },
    ],
  },
  {
    title: "Dealers & Tables",
    permissions: [
      { key: "dealer", label: "Table Management & Time Tracking" },
      { key: "dealerTableManagement", label: "Seat / Remove Players", requires: ["dealer"] },
      { key: "dealerCheckIn", label: "Verify Players at Table", requires: ["dealer"] },
      { key: "dealerAuditView", label: "Dealer Audit" },
    ],
  },
  {
    title: "Login Access",
    permissions: [
      { key: "webAccess", label: "Web Portal Login" },
      { key: "appAccess", label: "Mobile App Login" },
    ],
  },
  {
    title: "Super Admin",
    superAdminOnly: true,
    permissions: [{ key: "isSuperAdmin", label: "Super Admin (all pages, all clubs)" }],
  },
];

const ALL_PERMISSIONS = ROLE_PERMISSION_GROUPS.flatMap((group) => group.permissions);

export const ROLE_PERMISSION_KEYS = ALL_PERMISSIONS.map((permission) => permission.key);

// Every permission false, ready for a new role
export const emptyPermissions = () =>
  Object.fromEntries(ROLE_PERMISSION_KEYS.map((key) => [key, false]));

// Only the known permissions of a role document, as booleans
export const pickPermissions = (role = {}) =>
  Object.fromEntries(ROLE_PERMISSION_KEYS.map((key) => [key, role?.[key] === true]));

// Toggle one permission, keeping dependencies consistent
export const togglePermission = (permissions, key, checked) => {
  const next = { ...permissions, [key]: checked };
  if (checked) {
    const enable = (permissionKey) => {
      const permission = ALL_PERMISSIONS.find((item) => item.key === permissionKey);
      (permission?.requires || []).forEach((required) => {
        if (!next[required]) {
          next[required] = true;
          enable(required);
        }
      });
    };
    enable(key);
  } else {
    const disable = (permissionKey) => {
      ALL_PERMISSIONS.filter((item) => item.requires?.includes(permissionKey)).forEach((dependent) => {
        if (next[dependent.key]) {
          next[dependent.key] = false;
          disable(dependent.key);
        }
      });
    };
    disable(key);
  }
  return next;
};

// Super Admin always passes; everyone else needs the permission set to true
export const hasPermission = (role, permission) =>
  Boolean(role && (role.isSuperAdmin === true || role[permission] === true));
